// Pricing — fetch BCH / USD from a public CORS-friendly source once on boot,
// cache in memory, expose bchUsd() as a synchronous read. Used by the swap
// card to compute a "~$20 in the current FROM asset" default-amount hint
// (see updateAmountPlaceholder in main.ts).
//
// Design choices:
//   • Best-effort fetch. If the network call fails, we return the fallback
//     (a recent-ish BCH/USD value) rather than block the UI or throw. The
//     placeholder is a hint, not a settlement price.
//   • One fetch per session. The hint is illustrative; refreshing on every
//     asset change would be wasted requests.
//   • No on-chain oracle here. The wallet uses oracles.cash for signed price
//     feeds where it matters (FIATμ mint/redeem); the hint on the swap card
//     is decorative and doesn't need signed prices.

const FALLBACK_BCH_USD = 500;   // static rough anchor for offline / DNS-blocked / CoinGecko-down
const REQUEST_TIMEOUT_MS = 4_000;
const SOURCES: ReadonlyArray<{ url: string; extract: (j: unknown) => number | null }> = [
  {
    url: 'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin-cash&vs_currencies=usd',
    extract: (j) => {
      const v = (j as { 'bitcoin-cash'?: { usd?: number } })['bitcoin-cash']?.usd;
      return typeof v === 'number' && v > 0 ? v : null;
    },
  },
  {
    url: 'https://min-api.cryptocompare.com/data/price?fsym=BCH&tsyms=USD',
    extract: (j) => {
      const v = (j as { USD?: number }).USD;
      return typeof v === 'number' && v > 0 ? v : null;
    },
  },
];

let cachedBchUsd: number = FALLBACK_BCH_USD;
let fetchInFlight: Promise<number> | null = null;

/** Synchronous read — returns the most recent bchUsd or the fallback. */
export function bchUsd(): number { return cachedBchUsd; }

/** Kicks off a best-effort fetch. Idempotent; multiple calls share one
 *  in-flight promise. Resolves to the same number bchUsd() reports. */
export function refreshBchUsd(): Promise<number> {
  if (fetchInFlight) return fetchInFlight;
  fetchInFlight = (async () => {
    for (const src of SOURCES) {
      try {
        const controller = new AbortController();
        const t = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        const res = await fetch(src.url, { signal: controller.signal, cache: 'no-store' });
        clearTimeout(t);
        if (!res.ok) continue;
        const j = await res.json();
        const v = src.extract(j);
        if (v != null) { cachedBchUsd = v; return v; }
      } catch { /* try next */ }
    }
    return cachedBchUsd;   // keep fallback
  })().finally(() => { fetchInFlight = null; });
  return fetchInFlight;
}
